import {useState} from "react";
import "./FieldDateSwitch2.scss"
import {debounce} from "../../utils";
import {makeAutoObservable} from "mobx";

export interface FieldDateSwitch2Value {
    absolute?: string
    relative?: string
}

const numRegex = RegExp(/([\d]+)/)

class FocusStore {
    get focused(): boolean {
        return this._focused;
    }

    set focused(value: boolean) {
        this._focused = value;
    }

    private _focused: boolean = false

    constructor() {
        makeAutoObservable(this)
    }
}


interface FocusContext {
    focus: FocusStore
    setFocused: (b: boolean) => any
}

const unsetFocusedDebounced: (a: FocusContext) => any = debounce(({
                                                                      focus,
                                                                      setFocused
                                                                  }: FocusContext) => {
    if (!focus.focused) {
        setFocused(false)
    }
}, 200)


interface FieldDateSwitchProps {
    id?: string
    required?: boolean
    small?: boolean
    label?: string
    value?: FieldDateSwitch2Value
    invalid?: boolean
    disabled?: boolean
    help?: string
    placeholder?: string
    onChange?: (value: FieldDateSwitch2Value) => void
}

function durationTypDisplay(amount: string, type: string) {
    const singular = amount === '1'
    switch (type) {
        case 'm': {
            return singular ? 'Minute' : 'Minuten';
        }
        case 'h': {
            return singular ? 'Stunde' : 'Stunden';
        }
        case 'd': {
            return singular ? 'Tag' : 'Tage';
        }
    }
}

function FieldDateSwitch2({
                              id,
                              disabled,
                              label,
                              small,
                              invalid,
                              value,
                              required,
                              placeholder,
                              onChange
                          }: FieldDateSwitchProps) {
    const vA = value?.absolute ? isNaN(new Date(value?.absolute).valueOf()) ? '' : (new Date(value?.absolute)).toISOString().substring(0, 10) : ''


    const relVal = (value?.relative ? `${value?.relative}` : '').toLowerCase()
    const relDurationType = relVal.substring(relVal.length - 1) || 'd'
    const relValue = numRegex.test(relVal) ? numRegex.exec(relVal)![0] : ''
    const isRelative = !Boolean(value?.absolute)

    const dateType = isRelative ? 'relative' : 'absolute'

    const display = isRelative ?
        (relValue ? `${relValue} ${durationTypDisplay(relValue, relDurationType)}` : '') :
        (vA ? (new Date(vA).toLocaleDateString()) : '')


    const [focus] = useState(new FocusStore())
    const [focused, setFocused] = useState(false)

    return <>
        <label
            id={id}
            className={`field field-date-switch2 ${required ? 'required' : ''}`}
            onFocus={() => {
                setFocused(true)
                focus.focused = true
            }}
            onBlur={() => {
                focus.focused = false
                unsetFocusedDebounced({
                    focus,
                    setFocused
                })
            }}
        >
            {label && <label className={`label`}>{label}</label>}
            <div className={`field`}>
                <div className="control">
                    <input className={'input'} readOnly={true} value={display}/>
                </div>
            </div>
            {focused && <>
                <div style={{
                    position: 'relative',
                    marginTop: 'calc(calc(var(--bulma-block-spacing) * -1) + 0.1em)'
                }}>
                    <div style={{
                        position: 'absolute',
                        backgroundColor: 'var(--bulma-scheme-main)',
                        width: '14em'
                    }}>

                        <div className={"textarea"}>
                            <div className={'switch-container'}>
                                <label
                                    className={`switch ${dateType === 'relative' ? 'active' : ''}`}
                                    onClick={() => {
                                        if (!isRelative) {
                                            onChange && onChange({
                                                relative: 'P1D',
                                                absolute: undefined,
                                            })
                                        }
                                    }}
                                >
                                    <div className={'switch-action'}>Relativ</div>
                                    {isRelative &&
                                        <div className={'field-container'}>
                                            <div className={`field has-addons`}>
                                                <div className="control">
                                                    <input
                                                        className="input"
                                                        type={'number'}
                                                        min={0}
                                                        value={relValue}
                                                        placeholder={placeholder}
                                                        onChange={e => {

                                                            const s = relDurationType
                                                            const val = e.target.value
                                                            const out = s === 'd' ? `P${val}D` : (
                                                                s === 'h' ? `PT${val}H` : `PT${val}M`
                                                            )

                                                            onChange && onChange({
                                                                relative: val ? out : undefined
                                                            })
                                                        }}
                                                    />
                                                </div>
                                                <div className="control">
                                                    <span className="select">
                                                        <select
                                                            onChange={e => {
                                                                const s = e.target.value
                                                                const val = relValue
                                                                const out = s === 'd' ? `P${val}D` : (
                                                                    s === 'h' ? `PT${val}H` : `PT${val}M`
                                                                )

                                                                val && onChange && onChange({
                                                                    relative: out
                                                                })
                                                            }}
                                                            value={relDurationType}
                                                            style={{
                                                                width: '7em'
                                                            }}
                                                        >
                                                            <option
                                                                value={'d'}>{durationTypDisplay(relValue || '', 'd')}</option>
                                                            <option
                                                                value={'h'}>{durationTypDisplay(relValue || '', 'h')}</option>
                                                            <option
                                                                value={'m'}>{durationTypDisplay(relValue || '', 'm')}</option>
                                                        </select>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </label>
                                <label className={`switch ${dateType === 'absolute' ? 'active' : ''}`}
                                       onClick={() => {
                                           if (isRelative) {
                                               onChange && onChange({
                                                   relative: undefined,
                                                   absolute: (new Date()).toISOString(),
                                               })
                                           }
                                       }}
                                >
                                    <div className={'switch-action'}>Absolut</div>
                                    {!isRelative &&
                                        <div className={'field-container'}>
                                            <div className="control">
                                                <input
                                                    id={id ? `${id}-input` : undefined}
                                                    disabled={disabled}
                                                    className={`input is-fullwidth ${small ? 'is-small' : ''} ${invalid ? 'is-danger' : ''} ${required ? 'required' : ''}`}
                                                    type={"date"}
                                                    value={vA}
                                                    placeholder={placeholder}
                                                    onChange={e => {
                                                        const d = new Date(e.target.value)
                                                        onChange && onChange({
                                                            absolute: isNaN(d.valueOf()) ? undefined : d.toISOString()
                                                        })
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    }
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </>}
        </label>
    </>
}

export {
    FieldDateSwitch2
}

